import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface ChartModalProps {
  open: boolean;
  onClose: () => void;
  symbol: string;
}

// Using TradingView widget instead of Investing.com for better symbol support
// TradingView works directly with stock symbols without needing pair IDs

const ChartModal: React.FC<ChartModalProps> = ({ open, onClose, symbol }) => {
  const [loading, setLoading] = React.useState(true);
  
  // Simple TradingView widget with full toolbar for manual indicator addition
  // Using America/New_York timezone for Eastern Time (automatically handles EST/EDT)
  const chartUrl = `https://s.tradingview.com/widgetembed/?frameElementId=tradingview_${symbol}&symbol=${symbol}&interval=5&hidesidetoolbar=0&symboledit=1&saveimage=1&toolbarbg=F1F3F6&theme=light&style=1&timezone=America%2FNew_York&locale=en&utm_source=&utm_medium=widget_new&utm_campaign=chart&utm_term=${symbol}`;
  
  const handleIframeLoad = () => {
    setLoading(false);
  };
  
  // Reset loading state when modal opens
  React.useEffect(() => {
    if (open) {
      setLoading(true);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '600px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" component="div">
          {symbol} - Live Chart
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading chart for {symbol}...
            </Typography>
          </Box>
        )}
        
        <Box sx={{ width: '100%', height: '480px', display: 'flex', justifyContent: 'center' }}>
          <iframe
            src={chartUrl}
            width="100%"
            height="480"
            frameBorder="0"
            allowTransparency={true}
            marginWidth={0}
            marginHeight={0}
            onLoad={handleIframeLoad}
            style={{
              border: 'none',
              minWidth: '800px',
              opacity: loading ? 0 : 1,
              transition: 'opacity 0.3s ease'
            }}
            title={`${symbol} Live Chart`}
            allow="clipboard-write"
          />
        </Box>
        
        <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
          <Typography variant="caption" color="text.secondary">
            Real-time candlestick chart with 5-minute intervals • Powered by TradingView
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartModal; 